import { addDays, format } from 'date-fns';

/**
 * Utility functions for date formatting with timezone support
 */

/**
 * Formats a date string or Date object to Eastern timezone date string
 * @param date - Date string or Date object to format
 * @param format - Format type ('short' for MM/DD/YYYY, 'long' for MMM DD, YYYY)
 * @returns Formatted date string in Eastern timezone
 */
export const formatDateInEastern = (date: string | Date, format: 'short' | 'long' = 'short'): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;

  if (isNaN(dateObj.getTime())) {
    return '';
  }

  const options: Intl.DateTimeFormatOptions = {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: format === 'short' ? '2-digit' : 'short',
    day: '2-digit',
  };

  return dateObj.toLocaleDateString('en-US', options);
};

/**
 * Formats the TIME part of a timestamp in Eastern, e.g. "6:00 AM EDT".
 *
 * The zone abbreviation is rendered by Intl rather than hardcoded, so it reads
 * EDT in summer and EST in winter — a fixed "EST" label would be an hour wrong
 * for most of the year.
 *
 * @param date - Date string or Date object to format
 * @returns Formatted time string, or '' when the input isn't a valid date
 */
export const formatTimeInEastern = (date: string | Date): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;

  if (isNaN(dateObj.getTime())) {
    return '';
  }

  return dateObj.toLocaleTimeString('en-US', {
    timeZone: 'America/New_York',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });
};

/**
 * Formats a date-only string (e.g. "2026-04-08") for display without timezone conversion.
 * Unlike formatDateInEastern, this avoids the UTC shift that causes date-only strings
 * to display as the previous day in US timezones.
 * Use this for fields like accountingDate that are stored as date-only strings.
 */
export const formatDateOnly = (date: string, format: 'short' | 'long' = 'short'): string => {
  const parsed = parseDateAsLocal(date);

  if (isNaN(parsed.getTime())) {
    return '';
  }

  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: format === 'short' ? '2-digit' : 'short',
    day: '2-digit',
  };

  return parsed.toLocaleDateString('en-US', options);
};

const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Formats a value that may be EITHER a date-only string ("2026-04-08") or a full
 * timestamp ("2026-04-08T20:42:37.910Z"), choosing the right formatter for each.
 *
 * Highnote types `settlementDate` as a plain String and both shapes occur, so a
 * caller cannot know which it holds. A timestamp has to be converted to Eastern;
 * a date-only string must NOT be, because `new Date('2026-04-08')` is UTC
 * midnight and renders as the previous day in US timezones.
 */
export const formatDateOrTimestamp = (date: string | Date, format: 'short' | 'long' = 'short'): string =>
  typeof date === 'string' && DATE_ONLY_PATTERN.test(date)
    ? formatDateOnly(date, format)
    : formatDateInEastern(date, format);

/**
 * Parse a date string as a local-midnight Date, not UTC midnight.
 * Handles both "YYYY-MM-DD" and "YYYY-MM-DDT..." formats by stripping
 * any time component before parsing.
 */
export const parseDateAsLocal = (date: string): Date => {
  const dateOnly = date.includes('T') ? date.split('T')[0] : date;
  const [year, month, day] = dateOnly.split('-').map(Number);
  return new Date(year, month - 1, day);
};

/**
 * Formats a date string or Date object to Eastern timezone using date-fns format
 * This is a wrapper around date-fns format that handles timezone conversion
 * @param date - Date string or Date object to format
 * @param formatString - date-fns format string
 * @returns Formatted date string in Eastern timezone
 */
export const formatDateInEasternWithDateFns = (date: string | Date, formatString: string): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;

  if (isNaN(dateObj.getTime())) {
    return '';
  }

  // Convert to Eastern timezone by creating a new date with the timezone offset
  const easternDate = new Date(dateObj.toLocaleString('en-US', { timeZone: 'America/New_York' }));

  return format(easternDate, formatString);
};

const EASTERN_TIME_ZONE = 'America/New_York';

/**
 * Minutes to add to a UTC instant to get Eastern wall-clock time at that instant
 * (-240 during daylight time, -300 during standard time).
 */
const easternOffsetMinutes = (instant: Date): number => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: EASTERN_TIME_ZONE,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(instant);
  const part = (type: string): number => Number(parts.find((p) => p.type === type)?.value);
  const wallClockAsUtc = Date.UTC(
    part('year'),
    part('month') - 1,
    part('day'),
    part('hour'),
    part('minute'),
    part('second')
  );
  return Math.round((wallClockAsUtc - instant.getTime()) / 60000);
};

/**
 * The instant at which the calendar day of `day` (read in the browser's own
 * calendar, as a date picker produces it) begins in Eastern time.
 *
 * The platform displays every transaction date in Eastern
 * (`formatDateInEastern`) and the export Lambda already bounds its ranges in
 * Eastern (`date_range.py`). Building the table's search bounds in the
 * browser's zone instead made "July 1 to 31" include rows the table itself
 * labels August 1, for any user not in Eastern (SEE-1582, SEE-1385).
 */
export const easternDayStart = (day: Date): Date => {
  const midnightAsUtc = Date.UTC(day.getFullYear(), day.getMonth(), day.getDate());
  // The offset sampled at 00:00Z (the evening before, Eastern) is the offset at
  // Eastern midnight too: Eastern changes clocks at 02:00 local, after midnight.
  return new Date(midnightAsUtc - easternOffsetMinutes(new Date(midnightAsUtc)) * 60000);
};

/** The instant at which the calendar day AFTER `day` begins in Eastern time: an exclusive end bound. */
export const easternNextDayStart = (day: Date): Date => easternDayStart(addDays(day, 1));

/**
 * Today's calendar date in Eastern time, as a local-midnight Date so date-fns
 * calendar helpers (startOfMonth, subWeeks, ...) can be applied to it.
 */
export const easternToday = (now: Date = new Date()): Date => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: EASTERN_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now);
  const part = (type: string): number => Number(parts.find((p) => p.type === type)?.value);
  return new Date(part('year'), part('month') - 1, part('day'));
};

/** ISO instant one millisecond before the Eastern day of `day` begins: an inclusive `gt` start bound. */
export const easternInclusiveStart = (day: Date): string =>
  new Date(easternDayStart(day).getTime() - 1).toISOString();

/** ISO instant at which the Eastern day after `day` begins: an exclusive `lt` end bound. */
export const easternExclusiveEnd = (day: Date): string => easternNextDayStart(day).toISOString();

/**
 * Search bounds covering whole Eastern calendar days, from the day of `firstDate`
 * through the day of `lastDate` inclusive, in the shape the search Lambda reads.
 *
 * `search_spx_transactions.py` only reads `gt` and `lt` and intersects ranges by
 * comparing the ISO strings, so the inclusive start is expressed as "greater than
 * the millisecond before the day begins". eventDate is stored at millisecond
 * precision, so the two describe the same instants.
 */
export const easternDayBounds = (firstDate?: Date, lastDate?: Date): { gt?: string; lt?: string } => ({
  ...(firstDate ? { gt: easternInclusiveStart(firstDate) } : {}),
  ...(lastDate ? { lt: easternExclusiveEnd(lastDate) } : {}),
});
